import {defineStore} from 'pinia'
import {useAppMenuStore} from '@core/stores/app/appMenuStore'

const name = 'appMenuShortcut'

export const MAX_PINS = 8
export const MAX_RECENTS = 10

/** menuKeys présents dans l'arbre de menu visible (droits + mode candidat/recruteur). */
function collectKeys(nodes, out = new Set()) {
  (nodes || []).forEach(n => {
    if (n.menuKey) out.add(n.menuKey)
    collectKeys(n.nodes, out)
  })
  return out
}

/**
 * Module Raccourcis du tiroir : entrées épinglées (persistées serveur,
 * /appUserMenuPin) + écrans récemment consultés (journal d'activité serveur).
 * Tout est identifié par `menuKey` et résolu contre le menu chargé : une entrée
 * qui n'est plus visible (droit retiré, autre mode) est simplement masquée.
 */
export const useAppMenuShortcutStore = defineStore(name, {
  id: name,

  state: () => ({
    pins: [],        // [menuKey] ordonnés
    recents: [],     // [{menuKey, at}] du plus récent au plus ancien (/appActivity/recent)
    pinsLoaded: false,
  }),

  actions: {
    setPins(keys) {
      this.pins = (keys || []).slice(0, MAX_PINS)
      this.pinsLoaded = true
    },
    setRecents(list) {
      this.recents = list || []
    },
    clear() {
      this.pins = []
      this.recents = []
      this.pinsLoaded = false
    },
  },

  getters: {
    visibleKeys: () => collectKeys(useAppMenuStore().visibleMenus),

    isPinned: (state) => (menuKey) => state.pins.includes(menuKey),

    /**
     * Une entrée est raccourci-able si sa route n'attend aucun paramètre
     * (`/ace/user/detail/:userId`, `/ace/project/detail/:projectId`…) : ouverte
     * depuis un raccourci, sans contexte, une telle page resterait vide.
     */
    isShortcutable: () => (item) => {
      const route = item?.goTo || item?.path || ''
      return !!item?.menuKey && !!route && !/:[A-Za-z_]/.test(route)
    },

    isFull: (state) => state.pins.length >= MAX_PINS,

    /**
     * Entrée de menu (feuille navigable) correspondant à une route :
     * `route.name` = nom généré depuis le menu ; pour les panels (même nom de
     * route pour plusieurs entrées) on départage par `params.itemId`.
     */
    menuKeyForRoute: () => (route) => {
      if (!route?.name) return null
      const items = useAppMenuStore().items.filter(i => i.name === route.name && i.menuKey && i.goTo)
      if (items.length === 0) return null
      if (items.length === 1) return items[0].menuKey
      const itemId = route.params?.itemId
      const panel = items.find(i => i.mode === 'panel' && String(i.id) === String(itemId))
      return (panel || items[0]).menuKey
    },

    /** Entrée de menu enrichie (libellé du parent) ou null si non visible. */
    resolve() {
      const menuStore = useAppMenuStore()
      const visible = this.visibleKeys
      return (menuKey) => {
        if (!visible.has(menuKey)) return null
        const item = menuStore.retrieveItem(menuKey)
        if (!item || !item.name) return null
        // Routes paramétrées : jamais en épinglé ni en récent (y compris les pins déjà persistés)
        if (!this.isShortcutable(item)) return null
        const parent = item.prevId ? menuStore.retrieveItemById(item.prevId) : null
        return {...item, parentCode: parent?.code || null}
      }
    },

    pinnedItems() {
      return this.pins.map(k => this.resolve(k)).filter(Boolean)
    },

    recentItems() {
      return this.recents
        .map(r => {
          const item = this.resolve(r.menuKey)
          return item ? {...item, visitedAt: r.at} : null
        })
        .filter(Boolean)
        .slice(0, MAX_RECENTS)
    },
  },
})
